import type { Invoice } from "./types";
import {
  formatInvoiceListDate,
  normalizeInvoiceRecord,
  type InvoiceApiRecord,
} from "./invoiceListUtils";
import { inr } from "./utils";
import { extractPaginatedCount } from "./billingPaginationUtils";

export const DASHBOARD_LISTING_QUERY_KEY = ["dashboard-listing"] as const;

export interface DashboardCardsApiResponse {
  pending_approval?: number;
  paid_approval?: number;
  overdue_approval?: number;
  total_revenue?: string | number;
  pending_revenue?: string | number;
}

export interface DashboardListingApiItem {
  id?: string;
  invoice_number?: string;
  client_name?: string;
  project?: string;
  invoice_date?: string;
  billing_period_from?: string;
  billing_period_to?: string;
  subtotal?: string | number;
  tax_total?: string | number;
  grand_total?: string | number;
  status?: string;
  received_amount?: string | number;
  pending_amount?: string | number;
  sent?: string | null;
  paid?: string | null;
  payment_history?: Array<{ received_date?: string }>;
}

export interface DashboardListingApiResponse {
  success?: { code?: string; message?: string };
  data_listing?: DashboardListingApiItem[];
  cards?: DashboardCardsApiResponse;
}

export interface DashboardSummary {
  pendingApproval: string;
  paidApproval: string;
  overdueApproval: string;
  totalRevenue: string;
  pendingRevenue: string;
}

export interface DashboardListing {
  summary: DashboardSummary;
  invoices: Invoice[];
  count: number;
  page: number;
  pageSize: number;
}

function parseAmount(value?: string | number): number {
  if (value === undefined || value === null || value === "") {
    return 0;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function parseCount(value?: string | number): string {
  if (value === undefined || value === null || value === "") {
    return "0";
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? String(parsed) : String(value);
}

function parseRevenue(value?: string | number): string {
  if (value === undefined || value === null || value === "") {
    return "—";
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? inr(parsed) : "—";
}

function asDashboardPayload(response: unknown): DashboardListingApiResponse {
  if (!response || typeof response !== "object") {
    return {};
  }

  return response as DashboardListingApiResponse;
}

function normalizeDashboardListingItem(
  record: DashboardListingApiItem
): Invoice {
  const apiRecord: InvoiceApiRecord = {
    id: record.id,
    invoice_number: record.invoice_number,
    client_name: record.client_name,
    invoice_date: record.invoice_date,
    billing_period_from: record.billing_period_from,
    billing_period_to: record.billing_period_to,
    subtotal: record.subtotal,
    tax_total: record.tax_total,
    grand_total: record.grand_total,
    status: record.status,
  };

  const invoice = normalizeInvoiceRecord(apiRecord);

  if (invoice.period === "—" && record.invoice_date?.trim()) {
    invoice.period = formatInvoiceListDate(record.invoice_date);
  }

  invoice.received = parseAmount(record.received_amount);
  invoice.pending = parseAmount(record.pending_amount);
  invoice.project = record.project?.trim() ?? undefined;

  return invoice;
}

function extractSummary(response: unknown): DashboardSummary {
  const cards = asDashboardPayload(response).cards ?? {};

  return {
    pendingApproval: parseCount(cards.pending_approval),
    paidApproval: parseCount(cards.paid_approval),
    overdueApproval: parseCount(cards.overdue_approval),
    totalRevenue: parseRevenue(cards.total_revenue),
    pendingRevenue: parseRevenue(cards.pending_revenue),
  };
}

function extractInvoices(response: unknown): Invoice[] {
  const listing = asDashboardPayload(response).data_listing;

  if (!Array.isArray(listing)) {
    return [];
  }

  return listing
    .filter(item => item && typeof item === "object")
    .map(item => normalizeDashboardListingItem(item));
}

export function normalizeDashboardListing(
  response: unknown,
  page = 1,
  pageSize = 50
): DashboardListing {
  const invoices = extractInvoices(response);

  return {
    summary: extractSummary(response),
    invoices,
    count: extractPaginatedCount(response, invoices.length, page, pageSize),
    page,
    pageSize,
  };
}

export function getDefaultDashboardDateRange(): {
  startDate: string;
  endDate: string;
} {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0);

  const pad = (value: number) => String(value).padStart(2, "0");

  return {
    startDate: `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`,
    endDate: `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}`,
  };
}
